import { EmailTrigger } from "./enums/EmailTrigger";
import { ConditionGroup } from "./EventRule";
import { ActionType } from "./enums/ActionType";
import type { Filter } from "./Filter";

/**
 * Which guests an action runs against, RELATIVE to the trigger guest:
 *  - "triggering"     → the guest that triggered the workflow (default).
 *  - "mainGuest"      → the trigger's main guest (companion → up). If the
 *                       trigger already IS a main guest, falls back to itself.
 *  - "companions"     → the trigger's companions (main guest → down).
 *  - "allAffiliations"→ the whole party (main guest + all its companions).
 * Resolved server-side (main-consumer); an empty resolved set skips the action.
 */
export type TargetScope =
  | "triggering"
  | "mainGuest"
  | "companions"
  | "allAffiliations";

export const TARGET_SCOPES: TargetScope[] = [
  "triggering",
  "mainGuest",
  "companions",
  "allAffiliations",
];

/**
 * `config` stays an untyped map (the editor merges arbitrary keys per action
 * type). These keys are the shared TARGET selector — present on SEND_EMAIL and
 * UPDATE_GUEST today:
 *  - config.targetScope: TargetScope (absent ⇒ "triggering").
 *  - config.targetFilter: ConditionGroup narrowing the resolved set (optional).
 *
 * SEND_EMAIL on a SCHEDULED workflow only (stripped on any other trigger, see
 * `useWorkflowForm`; refused there by the backend):
 *  - config.sendClass: "ORGANISATIONAL" (absent/unknown ⇒ MARKETING).
 *  - config.sendClassAttested: true — mandatory with ORGANISATIONAL.
 *
 * SEND_SMS (trigger-driven pipes only; see {@link SendSmsActionConfig}):
 *  - config.recipientMode: "guest" | "fixed".
 *  - config.body: its own message, ≤ 3 parts, allowlisted placeholders only —
 *    OR config.smsTemplateId: an SMS template of the event (SMS templates
 *    wave). Exactly one is stored; read by type, see `smsTextSourceOf`.
 *  - config.targetScope / config.targetFilter: guest mode, as SEND_EMAIL.
 *  - config.fixedRecipientIds: fixed mode, 1..5 verified-number ids.
 *  - config.markAsContacted: guest mode only.
 */
export interface PipeAction {
  actionType: ActionType;
  config: Record<string, unknown>;
  order: number;
  /**
   * Transient client-side identity. NOT persisted — stripped in
   * `sanitizeActionTarget` before the wire. The editor keys the action list
   * by this so a React instance follows its action across delete/reorder
   * (index keys would let a component keep another action's local state).
   */
  _uid?: string;
}

/** Who a SEND_SMS goes to: the resolved guests, or company-verified numbers. */
export type SmsRecipientMode = "guest" | "fixed";
export const SMS_RECIPIENT_MODES: readonly SmsRecipientMode[] = ["guest", "fixed"];

/**
 * The SEND_SMS action config — the shared contract (assumptions "Action
 * config"). MIRROR: main-consumer `src/lib/workflow/model.ts`
 * (PipeActionSchema SEND_SMS branch), main-backend
 * `WorkflowResource.validateSmsActions`. `PipeAction.config` stays an untyped
 * map; read it through `readSmsConfig` (`lib/utils/smsConfig.ts`).
 */
export interface SendSmsActionConfig {
  recipientMode: SmsRecipientMode;
  /** Its own text (one language). Absent on a template action. */
  body?: string;
  /** An SMS template of the event (SMS templates wave). Absent on an own-text action. */
  smsTemplateId?: string;
  targetScope?: TargetScope;
  targetFilter?: ConditionGroup;
  fixedRecipientIds?: string[];
  markAsContacted?: boolean;
}

/**
 * A stored action as the WIRE may carry it (PB-AIE-3): `config` is optional
 * on both backend routes — `POST /api/v1/workflows/{e}` and the template twin
 * accept `{actionType, order}` alone, and the cockpit's own "Import from
 * Event" writes exactly that for every imported workflow. `PipeAction.config`
 * stays required (the editor derefs it in 30+ places); the ONE place a fetched
 * workflow enters the editor (`useWorkflowForm`) normalises a missing config
 * to `{}`. Anything that renders server data directly guards with `?.`.
 */
export type PipeActionWire = Omit<PipeAction, "config"> & {
  config?: Record<string, unknown>;
};

/** A fetched workflow whose actions may lack `config` — see `PipeActionWire`. */
export type WorkflowWire = Omit<Workflow, "actions"> & {
  actions: PipeActionWire[];
};

export type ScheduleMode = "ABSOLUTE" | "RELATIVE_TO_EVENT";
export type ScheduleAnchor = "EVENT_START" | "EVENT_END";
export type ScheduleStatus =
  | "PENDING"
  | "DISPATCHED"
  | "DONE"
  | "MISSED"
  | "SKIPPED";

/**
 * Schedule config for a SCHEDULED workflow. The editor only ever sends
 * the user-facing fields (mode / localAt / anchor / offsetMinutes); the
 * rest are server-authoritative and arrive read-only on fetched workflows.
 */
export interface WorkflowSchedule {
  mode: ScheduleMode;
  /** ABSOLUTE — wall-clock "YYYY-MM-DDTHH:mm", interpreted in the event timezone. */
  localAt?: string;
  /** RELATIVE_TO_EVENT */
  anchor?: ScheduleAnchor;
  /** RELATIVE_TO_EVENT — signed; negative = before the anchor. */
  offsetMinutes?: number;
  // ── server-authoritative (read-only) ──
  nextFireAt?: string;
  status?: ScheduleStatus;
  claimedAt?: string;
  enqueueConfirmed?: boolean;
  firedAt?: string;
  backgroundJobId?: string;
  /**
   * Why a run ended SKIPPED, when the consumer knows (Welle Z 1f). A closed
   * set on the backend; today only {@link SCHEDULE_SKIP_REASON_EMAIL_QUOTA_EXCEEDED}.
   * Written with the two figures below in the same CAS as `status: SKIPPED`;
   * absent on every other SKIPPED (e.g. disabled at fire time).
   */
  skipReason?: string;
  /** 1f: recipients the skipped run needed (the consumer's count). */
  quotaNeeded?: number;
  /** 1f: email quota left when the consumer decided, ≥ 0. */
  quotaRemaining?: number;
}

/** `schedule.skipReason` of a scheduled send the remaining email quota did not
 *  cover — the consumer skips it whole (Welle Z 1f). */
export const SCHEDULE_SKIP_REASON_EMAIL_QUOTA_EXCEEDED = "emailQuotaExceeded";
/** W8 W2-0: the creating role's guest scope names a field that no longer exists
 *  (deleted after the mailing was scheduled) — the consumer or the backend's
 *  delete cascade skipped the run rather than widen it to every guest. */
export const SCHEDULE_SKIP_REASON_RECIPIENT_SCOPE_STALE = "recipientScopeStale";
/** W8 W2-0: the stored scope could not be read (shape the consumer refuses). */
export const SCHEDULE_SKIP_REASON_RECIPIENT_SCOPE_MALFORMED = "recipientScopeMalformed";
/** W4-a (W9): `schedule.skipReason` of a mailing refused because the event's ACTIONS plugin was off
 *  (main-backend `WorkflowSchedule.SKIP_REASON_PLUGIN_DISABLED`, the consumer's twin). Re-armed by the
 *  plugin PUT while the fire time is still ahead. */
export const SCHEDULE_SKIP_REASON_PLUGIN_DISABLED = "pluginDisabled";

/**
 * W9 trigger-scope sibling (plan B-4): why the BACKEND switched a pipe off on its
 * own — the field-delete cascade writes {@link WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_STALE}
 * (a guest field the frozen audience names was deleted), the backfill's fail-closed
 * bucket writes {@link WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_BACKFILL} (the creator's
 * guest scope could not be placed). A closed set on the backend; the cockpit names the
 * two it knows and renders nothing for anything else.
 */
export const WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_STALE = "recipientScopeStale";
export const WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_BACKFILL = "recipientScopeBackfill";
export type WorkflowDisabledReason =
  | typeof WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_STALE
  | typeof WORKFLOW_DISABLED_REASON_RECIPIENT_SCOPE_BACKFILL
  | (string & {});

export interface Workflow {
  workflowId?: string;
  eventId?: string;
  templateId?: string;
  name: string;
  enabled: boolean;
  triggers: EmailTrigger[];
  conditions: ConditionGroup;
  actions: PipeAction[];
  /** Non-null only when `triggers` contains SCHEDULED. */
  schedule?: WorkflowSchedule;
  /**
   * W8 W2-0 — server-owned, read-only: the creating role's guest row scope,
   * snapshotted when a SCHEDULED mailing is created by a row-restricted role
   * and frozen from then on (the consumer pages only those rows). Absent on a
   * mailing created by an unrestricted role. Sent in a body it is ignored.
   */
  recipientScope?: Filter[] | null;
  /**
   * A PUT request flag, never stored or returned: drop the stored
   * `recipientScope` in this write (an OWNER / ADMIN only; refused for a
   * stored SCHEDULED mailing). Sent by the editor when a "Mail an Kontakt"
   * cannot keep the frozen guest scope (audit hop3 contract F1).
   */
  clearRecipientScope?: boolean;
  /** W8 W2-0 — server-owned: the API token a service account created it with. */
  authoredViaTokenId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Server-owned, read-only (like the `schedule.*` lifecycle fields): when the
   * pipe was last switched on / off. The backend stamps them on the
   * transition and strips any value sent in a body, so no payload builder
   * includes them. The consumer uses them to skip a trigger that fired while
   * the pipe was off, even if the pipe is on again by the time the queue
   * catches up.
   */
  enabledAt?: string;
  disabledAt?: string;
  /**
   * W9 trigger-scope sibling (plan B-4) — server-owned, read-only: set together
   * with `enabled: false` when the backend pauses the pipe by itself (see
   * {@link WorkflowDisabledReason}), `$unset` the next time the pipe is switched
   * on. Absent on a pipe a person paused and on every backend before the
   * 13.11. hop; the list shows the reason only while `enabled` is false.
   */
  disabledReason?: WorkflowDisabledReason;
}

/** Shape returned by getEventSectionPreview for "pipes" section */
export interface TemplateWorkflowPreview {
  included: boolean;
  sourceEventId: string;
  sourceEventName: string;
  name: string;
  enabled: boolean;
  triggers: string[];
  conditions?: Record<string, unknown>;
  /** The source's schedule (SCHEDULED workflows) — on a Java that carries it. */
  schedule?: WorkflowSchedule;
  /**
   * Each action's `actionType` and `order`, plus — on a Java that carries it —
   * a `config` map and, for SEND_EMAIL, the source template's
   * `emailTemplateName`. An older Java spreads the config out flat beside the
   * two (`emailTemplateIndex`, `markAsContacted`, the UPDATE_GUEST keys …).
   * Not a `PipeAction`: `importedWorkflowOf` builds the one the route binds.
   */
  actions: Record<string, unknown>[];
}
